import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import type { Location } from 'react-router-dom'
import {
  NavigateFunction,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'

import { getPlaylist } from '@disco/state-management/selectors/playlists'
import { getShortlistById } from '@disco/state-management/selectors/shortlists'
import { getUser } from '@disco/state-management/selectors/user'

import { getShortlistBaseRoute } from 'utils/routing'

import WarningDialog from 'components/modals/dialogs/warning-dialog'

import type { RootState } from 'store/configureStore'

import ShareWarningForm from '../share-warning-form'

interface OwnProps {
  params: {
    playlistId?: string
    shortlistId?: string
    channelId?: string
  }
  navigate: NavigateFunction
  location: Location
}

const mapStateToProps = (
  state: RootState,
  { params: { shortlistId, playlistId } }: OwnProps,
) => {
  let playlist = null

  if (shortlistId || playlistId) {
    playlist = playlistId
      ? getPlaylist(state, {
          id: parseInt(playlistId, 10),
        })
      : getShortlistById(state, {
          id: shortlistId!,
        })?.playlist
  }

  return {
    playlist,
    user: getUser(state),
  }
}

const connector = connect(mapStateToProps, {})

type PropsFromRedux = ConnectedProps<typeof connector>
type ShareWarningProps = PropsFromRedux & OwnProps

class ShareWarning extends React.Component<ShareWarningProps> {
  _onSubmit = () => {
    const { playlistId, shortlistId, channelId } = this.props.params
    const { location } = this.props

    const baseRoute = getShortlistBaseRoute(location)
    const returnPath = location?.state?.returnPath || '/'

    let pathname = ''
    if (playlistId) pathname = `/explorer/playlists/${playlistId || ''}/share`
    if (shortlistId)
      pathname = `${baseRoute}/shortlists/${shortlistId || ''}/share`
    if (channelId) pathname = `/explorer/channels/${channelId || ''}/share`

    this.props.navigate(pathname, {
      state: { returnPath },
    })
  }
  _onClose = () => {
    const { shortlistId } = this.props.params
    const { location } = this.props

    if (shortlistId) {
      const baseRoute = getShortlistBaseRoute(location)
      const returnPath = location?.state?.returnPath || '/'

      return this.props.navigate(
        `${baseRoute}/shortlists/${shortlistId}/saved`,
        {
          state: { returnPath },
        },
      )
    }

    // Use returnPath from location state, fallback to home
    const returnPath = location?.state?.returnPath || '/'
    this.props.navigate(returnPath)
  }

  render() {
    const hasTerritories = this.props.user.territories.length > 0
    const caption = hasTerritories ? 'Territory sharing' : 'Warning'
    return (
      <WarningDialog
        caption={caption}
        confirmButtonCaption="Confirm"
        onCancel={this._onClose}
        hideActions
      >
        <ShareWarningForm
          params={this.props.params}
          onSubmit={this._onSubmit}
          onDismiss={this._onClose}
        />
      </WarningDialog>
    )
  }
}

const ConnectedShareWarning = connector(ShareWarning)

// HOC to provide navigate function to class component
const ShareWarningWithNavigate = (props: any) => {
  const navigate = useNavigate()
  const location = useLocation()
  const params =
    useParams<{
      playlistId?: string
      shortlistId?: string
      channelId?: string
    }>()
  return (
    <ConnectedShareWarning
      {...props}
      navigate={navigate}
      params={params}
      location={location}
    />
  )
}

export default ShareWarningWithNavigate
